/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { graphql, compose, Query } from 'react-apollo'
import gql from 'graphql-tag'
import { withRouter } from 'react-router-dom'

import {
  Spinner,
  IdentityWellbeingStatement,
  ClientEmployment,
  IdentityBankAccounts,
  ClientNotes,
  IdentityContactDetails,
  ClientCredit,
  ClientInvestment,
  ClientInsurance,
  IdentityFiles,
  EmployerContacts,
  EmployerDivisions,
  Heading,
  SvgIcon,
  ContactSummary,
  ContactResponsibilities,
  ContactNotes,
  ClientLoanAndCreditApplications,
  IdentityMetric,
  InvestmentMetricsDisplay,
  MetricComparisonTable,
  Warning,
  EmployerSolutionProducts,
  EmployerEmployees,
  EarnbacksProductDetails,
  Account,
  IdentityWhatsAppSession,
  Payslips
} from '../'

import {
  Requests,
  Case,
  CaseList,
  PayrollRunList,
  Communications,
  Accounts,
  CreditProductDetail,
  InvestmentProductDetail,
  InsuranceProductDetail,
  Request,
  WhatsAppMessage,
  WithdrawalList,
  WithdrawalDetail
} from '../../containers'

import {
  decodeBase64,
  upperCaseFirstCharacter
} from '../../utils'

import {
  METRIC_CLIENT_DAILY_SUMMARY
} from '../../constants'

const setStateDefault = (activeTabIndex = 0) => ({
  casesOrderBy: '-created_at',
  payrollRunsOrderBy: '-paid_on',
  loanApplicationOrderBy: '-created_at',
  showCreateCaseModal: false
})


class IdentityDetail extends React.PureComponent {
  static propTypes = {
    identityId: PropTypes.string.isRequired,
    detail: PropTypes.string.isRequired,
    objectId: PropTypes.string
  }

  state = setStateDefault()

  renderDetail = (detail, objectId) => {
    let identityId = this.props.identityId
    let client = this.props.data.identity.client
    let currentPath = this.props.location.pathname

    const identity = this.props.data.identity

    // three metric related constants follow
    const metricObjectId = client
      ? this.props.data.identity.client.id
      : null
    const metricIdentityId = this.props.data.identity.id
    const metricSourceId = METRIC_CLIENT_DAILY_SUMMARY

    switch(detail) {
      case 'summary':
        if (identity.isClient) {
          return (
            <div className='slds-size_3-of-4 slds-col-rule_right' style={{ paddingTop: '2%', margin: 'auto' }}>
              <IdentityWellbeingStatement identity={identityId} />
            </div>
          )
        } else if (identity.isContact) {
          return (
            <div className='slds-size_3-of-4 slds-col-rule_right' style={{ paddingTop: '2%', margin: 'auto' }}>
              <ContactSummary contactId={identity.contacts.edges[0].node.id} />
            </div>
          )
        } else {
          return null
        }

      case 'earnbacks':
        return (
          <EarnbacksProductDetails identityId={identityId} />
        )

      case 'employment':
        return (
          <div className='slds-size_3-of-4 slds-col-rule_right' style={{ paddingTop: '2%', margin: 'auto' }}>
            <ClientEmployment
              client={client.id}
              employer={client.division.employer.id}
            />
            <ClientNotes client={client.id} />
          </div>
        )

      case 'contact':
        return (
          <div className='slds-size_3-of-4 slds-col-rule_right' style={{ paddingTop: '2%', margin: 'auto' }}>
            <IdentityContactDetails identityId={identityId} />
          </div>
        )

      case 'banking':
        return (
          <div style={{ padding: '2% 1%', margin: 'auto' }}>
            <IdentityBankAccounts identityId={identityId} />
          </div>
        )

      // products sub-tabs: credit, investments & insurance
      case 'credit':
        if (objectId) {
          return (
            <div className='u-bg-color-white'>
              <CreditProductDetail loanId={objectId} history={this.props.history} />
            </div>
          )
        } else {
          return (
            <Fragment>
              <ClientCredit
                client={client.id}
                payrollCycle={client.payrollcycle
                  ? client.payrollcycle.cycle
                  : null
                }
                className='slds-m-bottom_x-large'
              />
              <div className='slds-m-top_x-large slds-m-bottom_xx-large slds-m-left_medium slds-m-right_medium'>
                <Heading marginBottom={5}>
                  <b>Requests & applications</b>
                </Heading>
                <ClientLoanAndCreditApplications
                  clientId={client.id}
                  orderBy={this.state.loanApplicationOrderBy}
                  changeSort={orderBy => this.setState({ loanApplicationOrderBy: orderBy })}
                />
              </div>
            </Fragment>
          )
        }

      case 'investments':
        if (objectId) {
          return (
            <div className='u-bg-color-white'>
              <InvestmentProductDetail
                clientInvestmentId={objectId}
                history={this.props.history}
              />
            </div>
          )
        } else {
          return (
            <ClientInvestment
              client={client.id}
              className='slds-m-bottom_x-large'
            />
          )
        }

      case 'withdrawals':
        if (objectId) {
          return (
            <div className='u-bg-color-white'>
              <WithdrawalDetail
                withdrawalId={objectId}
              />
            </div>
          )
        } else {
          return (
            <WithdrawalList
              identityId={identityId}
              className='slds-m-bottom_x-large'
            />
          )
        }

      case 'insurance':
      if (objectId) {
        return (
          <div className='u-bg-color-white'>
            <InsuranceProductDetail clientFuneralPolicyId={objectId} history={this.props.history} />
          </div>
        )
      } else {
        return (
          <ClientInsurance
            client={client.id}
            className='slds-m-bottom_x-large'
          />
        )
      }

      case 'requests':
        if (objectId) {
          return <Request id={objectId} />
        } else {
          return (
            <Requests
              title={null}
              identityId={identityId}
              statusFilterInSidebar={true}
              showRequestStatusColumn={true}
              updatedAtSortDefaultDesc={true}
            />
          )
        }

      case 'cases':
        if (objectId) {
          return <Case id={objectId} className='slds-border_top' />
        } else {
          return (
            <CaseList
              affectsId={identityId}
              urlPath={currentPath}
              statusFilterInSidebar={true}
              recordsPerPage={10}
              title={null}
              showStatusColumn={true}
              autoAssignToCaseHandler={false}
            />
          )
        }

      // payroll
      case 'payrollruns':
        return (
          <PayrollRunList
            title={null}
            employerId={client.division.employer.id}
            identityId={identityId}
            payrollCycleId={client.payrollcycle.id}
            statusFilterInSidebar={true}
            recordsPerPage={10}
          />
        )
      case 'payslips':
        return (
          <Fragment>
            <div className='slds-text-color_weak'>
              <SvgIcon
                variant='utility'
                symbol='info'
                size='small'
                colour='light'
                className='slds-p-around_small slds-p-left_medium'
              />
              The following paydays have proof of income in the form of client or employer submitted payslip data.
            </div>
            <Payslips identityId={identityId} recordsPerPage={10} />
          </Fragment>
        )

      case 'documents':
        return <IdentityFiles identity={identityId} />

      // comms
      case 'messages':
      case 'emails':
      case 'whatsapp':
        return (
          <Query
            query={
              gql`
                query($uuid: UUID!, $addressType: String!) {
                  addresses(identity_Id: $uuid, addressType: $addressType) {
                    edges {
                      node {
                        id
                        addressType
                      }
                    }
                  }
                }
              `
            }
            variables={{
              uuid: decodeBase64(identityId),
              addressType: detail === 'emails'
                ? 'email'
                : 'msisdn'
            }}>
            {({ loading, error, data }) => {
              if (loading || error) {
                return null
              }

              let addresses = data.addresses.edges
              if (addresses.length > 0) {
                return (
                  <div className='u-bg-color-white'>
                    {detail === 'whatsapp' &&
                      <IdentityWhatsAppSession
                        identityId={identityId}
                        title='Session info'
                        className='slds-p-left_small slds-p-top_small'
                      />
                    }
                    {objectId
                      ? <WhatsAppMessage messageId={objectId} />
                      : <Communications
                          identityId={identityId}
                          communicationType={detail === 'whatsapp'
                            ? 'WhatsApp'
                            : upperCaseFirstCharacter(detail)  // Messages / Emails
                          }
                          urlPath={currentPath}
                          title={null}
                          noStatusFilter={true}
                          key={detail}
                        />
                      }
                  </div>
                )
              } else {
                return (
                  <div className='slds-m-top_large slds-m-left_large'>
                    <SvgIcon
                      variant='utility'
                      symbol='info'
                      colour='light'
                      size='small'
                      className='slds-m-right_medium'
                    />
                    {`Identity has no ${detail === 'messages' ? 'cellphone' : 'email'} address.`}
                  </div>
                )
              }
            }}
          </Query>
        )

      case 'accounts':
        if (objectId) {
          return <Account id={objectId} />
        } else {
          return (
            <Accounts
              identityId={identityId}
              title={null}
              urlPath={currentPath}
            />
          )
        }


      case 'contacts':
        return (
          <EmployerContacts
            employerId={decodeBase64(this.props.data.identity.employer.id)}
            employer={this.props.data.identity.employer.id}
            className='slds-m-around_x-small'
          />
        )

      case 'divisions':
        return <EmployerDivisions employer={this.props.data.identity.employer.id} />

      case 'employees':
        return <EmployerEmployees employerId={this.props.data.identity.employer.id} />

      case 'payrollcycles':
        if (objectId) {
          return (
            <div className='slds-p-top_medium u-bg-color-white'>
              <PayrollRunList
                title={
                  <Heading marginBottom={5} className='u-font-weight-bold'>
                    Payroll runs
                  </Heading>
                }
                employerId={identity.employer.id}
                payrollCycleId={objectId}
                statusFilterInSidebar={true}
                initialStatusFilterValue='all'
                recordsPerPage={10}
              />
            </div>
          )
        } else {
          return null
        }

      case 'responsibilities':
        let contactId = this.props.data.identity.contacts.edges[0].node.id
        return (
          <div className='slds-size_3-of-4 slds-col-rule_right' style={{ paddingTop: '2%', margin: 'auto' }}>
            <ContactResponsibilities contactId={contactId} />
            <ContactNotes contactId={contactId} />
          </div>
        )

      // insights sub-tabs: latest, comparison
      case 'latest':
        return (
          <IdentityMetric
            identityId={this.props.data.identity.id}
            metricSourceId={METRIC_CLIENT_DAILY_SUMMARY}>
            {metric => (
              metric
                ? <InvestmentMetricsDisplay
                    description={metric.description}
                    createdAt={metric.createdAt}
                    metricResult={JSON.parse(metric.result)['Investments']}
                  />
                : <Warning className='slds-m-around_medium'>
                    No metrics could be found to generate report from.
                  </Warning>
            )}
          </IdentityMetric>
        )

      case 'comparison':
        return (
          <MetricComparisonTable
            objectId={metricObjectId}
            identityId={metricIdentityId}
            metricSourceId={metricSourceId}
            className='slds-m-around_medium'
            renderTemplate={this.getMetricComparisonRenderTemplate()}
          />
        )

      case 'solution':
        return (
          <EmployerSolutionProducts identityId={identityId} className='slds-p-around_medium' />
        )

      default:
        return null
    }
  }

  getTypicalLongtermInvestmentMetricsByPayee = (payee = 'Client') => (
    {
      "Name": "CharField",
      "Units": "DecimalField",
      "Unit Price": "CurrencyField",
      "Balance - Bucket": "CharField",
      "Balance - Invested": "CurrencyField",
      "Balance - In Process": "CurrencyField",
      [`Contribution - ${payee}`]: "CurrencyField",
      [`Contribution Monthly - ${payee}`]: "CurrencyField",
      [`Contribution Bucket - ${payee}`]: "CurrencyField",
      "Contributions In Process - Count": "NumberField",
      "Contributions In Process - Value": "CurrencyField",
      "Contributions Count - Last Day": "NumberField",
      "Contributions Value - Last Day": "CurrencyField",
      "Contributions Count - Last 7 Days": "NumberField",
      "Contributions Value - Last 7 Days": "CurrencyField",
      "Contributions Count - Last 30 Days": "NumberField",
      "Contributions Value - Last 30 Days": "CurrencyField",
      "Contributions Count - Last 90 Days": "NumberField",
      "Contributions Value - Last 90 Days": "CurrencyField",
      "Withdrawals Count - Last Day": "NumberField",
      "Withdrawals Value - Last Day": "CurrencyField",
      "Withdrawals Count - Last 7 Days": "NumberField",
      "Withdrawals Value - Last 7 Days": "CurrencyField",
      "Withdrawals Count - Last 30 Days": "NumberField",
      "Withdrawals Value - Last 30 Days": "CurrencyField",
      "Withdrawals Count - Last 90 Days": "NumberField",
      "Withdrawals Value - Last 90 Days": "CurrencyField"
    }
  )

  getMetricComparisonRenderTemplate = () => {
    const typicalCreditProductFields = {
      "Number - Active": "NumberField",
      "Capital Issued - Active": "CurrencyField",
      "Outstanding Balance - Active": "CurrencyField",
      "Loans Issued Count - All Time": "NumberField",
      "Loans Issued Value - All Time": "CurrencyField",
      "Loans Issued Count - Last Day": "NumberField",
      "Loans Issued Value - Last Day": "CurrencyField",
      "Loans Issued Count - Last 7 Days": "NumberField",
      "Loans Issued Value - Last 7 Days": "CurrencyField",
      "Loans Issued Count - Last 30 Days": "NumberField",
      "Loans Issued Value - Last 30 Days": "CurrencyField",
      "Loans Issued Count - Last 90 Days": "NumberField",
      "Loans Issued Value - Last 90 Days": "CurrencyField"
    }

    const typicalShortTermInvestmentFields = {
      "Name": "CharField",
      "Units": "DecimalField",
      "Unit Price": "CurrencyField",
      "Balance - Bucket": "CharField",
      "Balance - Invested": "CurrencyField",
      "Balance - In Process": "CurrencyField",
      "Contribution - Client": "CurrencyField",
      "Contribution - Employer": "CurrencyField",
      "Contribution Monthly - Client": "CurrencyField",
      "Contribution Monthly - Employer": "CurrencyField",
      "Contribution Bucket - Client": "CurrencyField",
      "Contribution Bucket - Employer": "CurrencyField",
      "Contributions In Process - Count": "NumberField",
      "Contributions In Process - Value": "CurrencyField",
      "Contributions Count - Last Day": "NumberField",
      "Contributions Value - Last Day": "CurrencyField",
      "Contributions Count - Last 7 Days": "NumberField",
      "Contributions Value - Last 7 Days": "CurrencyField",
      "Contributions Count - Last 30 Days": "NumberField",
      "Contributions Value - Last 30 Days": "CurrencyField",
      "Contributions Count - Last 90 Days": "NumberField",
      "Contributions Value - Last 90 Days": "CurrencyField",
      "Withdrawals Count - Last Day": "NumberField",
      "Withdrawals Value - Last Day": "CurrencyField",
      "Withdrawals Count - Last 7 Days": "NumberField",
      "Withdrawals Value - Last 7 Days": "CurrencyField",
      "Withdrawals Count - Last 30 Days": "NumberField",
      "Withdrawals Value - Last 30 Days": "CurrencyField",
      "Withdrawals Count - Last 90 Days": "NumberField",
      "Withdrawals Value - Last 90 Days": "CurrencyField"
    }

    return {
      "Picsa": {
        "Solution": {
          "Package": "CharField"
        },
        "Membership": "CharField",
        "EarnBacks": "CharField",
        "Employment": {
          "Status": "CharField"
        },
        "Payroll": {
          "Last Approved": "DateField",
          "Last Receipted": "DateField",
          "Frequency": "Once a month"
        },
        'Health Level': "NumberField"
      },
      "Credit": {
        "Cash": typicalCreditProductFields,
        "Retail": typicalCreditProductFields,
        "Emergency": typicalCreditProductFields,
        "Consolidation": typicalCreditProductFields,
        "Payments Approved Count - Last Day": "NumberField",
        "Payments Approved Value - Last Day": "CurrencyField",
        "Payments Approved Count - Last 7 Days": "NumberField",
        "Payments Approved Value - Last 7 Days": "CurrencyField",
        "Payments Approved Count - Last 30 Days": "NumberField",
        "Payments Approved Value - Last 30 Days": "CurrencyField",
        "Payments Approved Count - Last 90 Days": "NumberField",
        "Payments Approved Value - Last 90 Days": "CurrencyField"
      },
      "Investments": {
        "Long-term": {
          "Units": "DecimalField",
          "Unit Price": "CurrencyField",
          "Balance - Bucket": "CharField",
          "Balance - Invested": "CurrencyField",
          "Balance - In Process": "CurrencyField",
          "Contribution - Client": "CurrencyField",
          "Contribution - Employer": "CurrencyField",
          "Contributions In Process - Count": "NumberField",
          "Contributions In Process - Value": "CurrencyField",
          "Contribution Bucket - Client": "CurrencyField",
          "Contribution Bucket - Employer": "CurrencyField",
          "Contributions Count - Last Day": "NumberField",
          "Contributions Value - Last Day": "CurrencyField",
          "Contributions Count - Last 7 Days": "NumberField",
          "Contributions Value - Last 7 Days": "CurrencyField",
          "Contributions Count - Last 30 Days": "NumberField",
          "Contributions Value - Last 30 Days": "CurrencyField",
          "Contributions Count - Last 90 Days": "NumberField",
          "Contributions Value - Last 90 Days": "CurrencyField",
          "Withdrawals Count - Last Day": "NumberField",
          "Withdrawals Value - Last Day": "CurrencyField",
          "Withdrawals Count - Last 7 Days": "NumberField",
          "Withdrawals Value - Last 7 Days": "CurrencyField",
          "Withdrawals Count - Last 30 Days": "NumberField",
          "Withdrawals Value - Last 30 Days": "CurrencyField",
          "Withdrawals Count - Last 90 Days": "NumberField",
          "Withdrawals Value - Last 90 Days": "CurrencyField"
        },
        "Short-term": typicalShortTermInvestmentFields,
        "Emergency Savings": typicalShortTermInvestmentFields,
        "Investor Type": "CharField",
        "Long-term - Employee": this.getTypicalLongtermInvestmentMetricsByPayee(),
        "Long-term - Employer": this.getTypicalLongtermInvestmentMetricsByPayee('Employer'),
        "Payments Approved Count - Last Day": "NumberField",
        "Payments Approved Value - Last Day": "CurrencyField",
        "Payments Approved Count - Last 7 Days": "NumberField",
        "Payments Approved Value - Last 7 Days": "CurrencyField",
        "Payments Approved Count - Last 30 Days": "NumberField",
        "Payments Approved Value - Last 30 Days": "CurrencyField",
        "Payments Approved Count - Last 90 Days": "NumberField",
        "Payments Approved Value - Last 90 Days": "CurrencyField"
        }
      }
  }

  render() {
    if (this.props.data.loading) {
      return <Spinner className='slds-is-relative' />
    }

    if (this.props.data.error) {
      return <h1>Oh dear! We have an issue</h1>
    }

    return this.renderDetail(this.props.detail, this.props.objectId)
  }
}

const getIdentityDetails = gql`
  query($id: ID!) {
    identity(id: $id) {
      id
      fullName
      identityValue
      membershipStatus
      isClient
      isContact
      client {
        id
        status
        division {
          id
          employer {
            id
            friendlyName
          }
        }
        payrollcycle {
          id
          cycle
        }
      }
      contacts {
        edges {
          node {
            id
          }
        }
      }
    }
  }
`

const withData =
  graphql(getIdentityDetails, {
    options: ({ identityId }) => ({
      variables: {
        id: identityId
      }
    })
  })

export default compose(
  withData,
  withRouter
)(IdentityDetail)



// WEBPACK FOOTER //
// ./src/components/identity-detail/index.js